import React, { useState } from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';

const faqs = [
  {
    category: 'Ordering & Customization',
    questions: [
      {
        q: 'How do I know which shaft flex is right for me?',
        a: 'We recommend using our AI Club Fitting Wizard for personalized recommendations. Generally, shaft flex is based on your swing speed: under 85 mph = Regular, 85-100 mph = Stiff, over 100 mph = X-Stiff. Our wizard will ask about your swing characteristics to give you a more accurate recommendation.'
      },
      {
        q: 'Can I change my order after it\'s placed?',
        a: 'Since our custom clubs are built to order, changes can only be made within 2 hours of placing your order. Contact us immediately at nbond@tortlab.com if you need to make changes.'
      },
      {
        q: 'What does "pured" mean?',
        a: 'Puring (also called spine alignment) is a process that locates the most stable orientation of a shaft and aligns it for optimal performance. This can help improve consistency and feel. We offer puring as an add-on service for an additional $50.'
      },
      {
        q: 'Do you offer club fitting services?',
        a: 'Yes! We offer both our AI-powered Club Fitting Wizard (free) and in-person fitting consultations at our Scottsdale location. Contact us to schedule an in-person fitting.'
      }
    ]
  },
  {
    category: 'Shipping & Delivery',
    questions: [
      {
        q: 'How long does it take to receive my custom club?',
        a: 'Custom orders typically take 5-7 business days to build, plus 3-5 business days for shipping. Total delivery time is usually 8-12 business days for US orders.'
      },
      {
        q: 'Do you ship internationally?',
        a: 'Yes, we ship worldwide. International orders may take 7-14 business days for delivery. Please note that customers are responsible for any customs duties or import taxes.'
      },
      {
        q: 'Is there free shipping?',
        a: 'We offer free standard shipping on all orders over $500 within the continental United States.'
      }
    ]
  },
  {
    category: 'Returns & Warranty',
    questions: [
      {
        q: 'What is your return policy?',
        a: 'We offer a 30-day satisfaction guarantee on unused items in original packaging. Custom-built clubs that have been used are not eligible for return unless there is a manufacturing defect.'
      },
      {
        q: 'Do your products come with a warranty?',
        a: 'All our shafts come with the manufacturer\'s warranty, typically 1-2 years against defects. Our assembly work is guaranteed for the life of the club.'
      },
      {
        q: 'What if my club arrives damaged?',
        a: 'If your order arrives damaged, contact us within 48 hours with photos of the damage. We\'ll send a replacement at no additional cost.'
      }
    ]
  },
  {
    category: 'Payment & Pricing',
    questions: [
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover) through our secure checkout powered by Stripe.'
      },
      {
        q: 'Are prices listed in USD?',
        a: 'Yes, all prices on our website are in US Dollars (USD).'
      },
      {
        q: 'Do you offer financing or payment plans?',
        a: 'We currently do not offer financing options, but we\'re working on adding this feature in the future.'
      }
    ]
  }
];

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-[#E8E4DC] last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left"
      >
        <span className="font-medium text-[#2C3E2D] pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-[#5A6B5C] flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-[#5A6B5C] flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 pr-8">
          <p className="text-[#5A6B5C]">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  const { tenantPath } = useTenant();
  
  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Header />
      
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 
            className="text-4xl md:text-5xl font-bold text-[#2C3E2D] mb-4"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Frequently Asked Questions
          </h1>
          <p className="text-[#5A6B5C] text-lg mb-12">
            Find answers to common questions about our products and services.
          </p>

          <div className="space-y-8">
            {faqs.map((category, index) => (
              <div key={index} className="bg-white rounded-xl border border-[#E8E4DC] overflow-hidden">
                <div className="bg-[#F5F3EF] px-6 py-4">
                  <h2 className="text-lg font-bold text-[#2C3E2D]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {category.category}
                  </h2>
                </div>
                <div className="px-6">
                  {category.questions.map((faq, faqIndex) => (
                    <FAQItem key={faqIndex} question={faq.q} answer={faq.a} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-[#4A6741] p-8 rounded-xl text-center">
            <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Still have questions?
            </h2>
            <p className="text-white/80 mb-6">Our team is here to help with any questions you might have.</p>
            <a 
              href={tenantPath("/contact")} 
              className="inline-flex items-center space-x-2 h-12 px-8 bg-white text-[#4A6741] font-bold uppercase tracking-wider rounded-lg hover:bg-[#F5F3EF] transition-all"
            >
              <span>Contact Us</span>
            </a>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
